/**
 * Empty Cart Component
 */

'use client';

import styled from '@emotion/styled';
import { Button, Text } from '@template-project/afrodita-components';
import { ShoppingBag } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  EMPTY_CART_CONTINUE_SHOPPING,
  EMPTY_CART_DESCRIPTION,
  EMPTY_CART_TITLE,
} from './i18.keys';
import { CartHeader } from '../CartHeader/CartHeader';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.space['3xl']} ${({ theme }) => theme.space.lg};
  text-align: center;
  flex: 1;
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.space.lg};
`;

const Title = styled(Text)`
  margin-bottom: ${({ theme }) => theme.space.sm};
`;

const Description = styled(Text)`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.space['2xl']};
  max-width: 280px;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  max-width: 280px;
`;

export interface EmptyCartProps {
  onClose: () => void;
  onContinueShopping?: () => void;
  shopLink?: string;
}

export function EmptyCart({ onClose, onContinueShopping, shopLink = '/products' }: EmptyCartProps) {
  const { t } = useTranslation('sidecart');

  const handleContinueShopping = () => {
    if (onContinueShopping) {
      onContinueShopping();
    }
    if (typeof window !== 'undefined' && shopLink) {
      window.location.assign(shopLink);
    }
  };

  return (
    <Container>
      <CartHeader itemCount={0} onClose={onClose} />
      <Content>
        <IconWrapper>
          <ShoppingBag size={40} aria-hidden />
        </IconWrapper>
        <Title variant="heading6">
          {t(EMPTY_CART_TITLE)}
        </Title>
        <Description variant="body">
          {t(EMPTY_CART_DESCRIPTION)}
        </Description>
        <ButtonWrapper>
          <Button
            variant="secondary"
            fullWidth
            onClick={handleContinueShopping}
          >
            {t(EMPTY_CART_CONTINUE_SHOPPING)}
          </Button>
        </ButtonWrapper>
      </Content>
    </Container>
  );
}
